import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ProgressRing } from "@/components/common/ProgressRing";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { ArrowLeft, Trash, Megaphone, Paperclip, ChatCircle } from "@phosphor-icons/react";

function money(v) { if (!v) return "₹0"; if (v >= 100000) return "₹" + (v / 100000).toFixed(1) + "L"; return "₹" + (v / 1000).toFixed(0) + "K"; }

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isApprover, user } = useAuth();
  const [confirm, setConfirm] = useState(false);
  const [comment, setComment] = useState("");

  const { data: c, isLoading } = useQuery({ queryKey: ["campaign", id], queryFn: async () => (await api.get(`/resource/campaigns/${id}`)).data });
  const { items: tasks } = useResource("tasks");
  const { items: influencers } = useResource("influencers");
  const { items: ads } = useResource("ads");
  const { items: approvals } = useResource("approvals");
  const { items: activity } = useResource("activity");
  const { data: comments = [] } = useQuery({ queryKey: ["comments", id], queryFn: async () => (await api.get("/resource/comments")).data });

  if (isLoading || !c) return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-violet border-t-transparent" /></div>;

  const relTasks = tasks.filter((t) => t.campaign === c.name);
  const relAds = ads.filter((a) => a.brand === c.brand);
  const relInfl = influencers.filter((i) => i.brand === c.brand).slice(0, 4);
  const relApprovals = approvals.filter((a) => a.brand === c.brand);
  const campComments = comments.filter((x) => x.entity_id === id);
  const spentPct = c.approved_budget ? Math.round((c.actual_spend / c.approved_budget) * 100) : 0;

  const del = async () => { await api.delete(`/resource/campaigns/${id}`); toast.success("Campaign deleted"); navigate("/campaigns"); };
  const addComment = async () => {
    if (!comment.trim()) return;
    await api.post("/resource/comments", { entity: "campaign", entity_id: id, text: comment, author: user?.name, avatar: user?.avatar });
    setComment(""); qc.invalidateQueries({ queryKey: ["comments", id] }); toast.success("Comment added");
  };

  const KPI = ({ label, value }) => (<div className="rounded-2xl border border-border bg-surface-raised p-4"><p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p><p className="mt-1 font-display text-xl font-black">{value}</p></div>);

  return (
    <div>
      <button onClick={() => navigate("/campaigns")} className="mb-4 flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground" data-testid="back-btn"><ArrowLeft size={16} /> Campaigns</button>

      <div className="mb-6 overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-brand-violet/15 to-surface-raised p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-3"><span className="h-3 w-3 rounded-full" style={{ background: c.color }} /><span className="font-mono text-xs text-muted-foreground">{c.code}</span><StatusBadge status={c.status} /></div>
            <h1 className="font-display text-3xl font-black" data-testid="page-title">{c.name}</h1>
            <p className="mt-1 text-muted-foreground">{c.brand} · {c.objective} · {c.country}</p>
          </div>
          {isApprover && <Button variant="outline" onClick={() => setConfirm(true)} data-testid="campaign-delete" className="rounded-full text-brand-pink"><Trash size={16} className="mr-1" /> Delete</Button>}
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-4 flex flex-wrap rounded-full">
          {["overview", "budget", "tasks", "influencers", "ads", "approvals", "comments", "activity"].map((t) => <TabsTrigger key={t} value={t} className="rounded-full capitalize" data-testid={`tab-${t}`}>{t}</TabsTrigger>)}
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <KPI label="Planned Budget" value={money(c.planned_budget)} />
            <KPI label="Approved" value={money(c.approved_budget)} />
            <KPI label="Actual Spend" value={money(c.actual_spend)} />
            <KPI label="Revenue" value={money(c.actual_revenue)} />
            <KPI label="ROAS" value={`${c.roas || 0}x`} />
            <KPI label="Expected Rev" value={money(c.expected_revenue)} />
            <KPI label="Audience" value={c.audience || "—"} />
            <KPI label="Platforms" value={(c.platforms || []).join(", ") || "—"} />
          </div>
          <div className="mt-4 rounded-2xl border border-border bg-surface-raised p-6"><p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">Brief</p><p className="text-sm leading-relaxed">{c.brief}</p></div>
        </TabsContent>

        <TabsContent value="budget">
          <div className="flex items-center gap-6 rounded-2xl border border-border bg-surface-raised p-6">
            <ProgressRing value={spentPct} size={100} stroke={9} color="#7C3AED" />
            <div className="space-y-1 text-sm">
              <p className="flex justify-between gap-8"><span className="text-muted-foreground">Planned</span><span className="font-mono">{money(c.planned_budget)}</span></p>
              <p className="flex justify-between gap-8"><span className="text-muted-foreground">Approved</span><span className="font-mono">{money(c.approved_budget)}</span></p>
              <p className="flex justify-between gap-8"><span className="text-muted-foreground">Actual spend</span><span className="font-mono">{money(c.actual_spend)}</span></p>
              <p className="flex justify-between gap-8"><span className="text-muted-foreground">Revenue</span><span className="font-mono text-lime-400">{money(c.actual_revenue)}</span></p>
            </div>
          </div>
        </TabsContent>

        {[["tasks", relTasks, (t) => <><p className="font-medium">{t.title}</p><StatusBadge status={t.status} /></>],
          ["ads", relAds, (a) => <><p className="font-medium">{a.name}</p><StatusBadge status={a.status} /></>],
          ["approvals", relApprovals, (a) => <><p className="font-medium">{a.title}</p><StatusBadge status={a.status} /></>]].map(([key, list, render]) => (
          <TabsContent key={key} value={key}>
            <div className="space-y-2">
              {list.length === 0 ? <p className="text-sm text-muted-foreground">No linked {key}.</p> : list.map((it) => <div key={it.id} className="flex items-center justify-between rounded-xl border border-border bg-surface-raised p-4">{render(it)}</div>)}
            </div>
          </TabsContent>
        ))}

        <TabsContent value="influencers">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {relInfl.map((i) => (<div key={i.id} className="flex items-center gap-3 rounded-xl border border-border bg-surface-raised p-4"><Avatar className="h-10 w-10"><AvatarImage src={i.avatar} /><AvatarFallback>{i.name[0]}</AvatarFallback></Avatar><div><p className="font-medium">{i.name}</p><p className="text-xs text-muted-foreground">{i.platform} · {i.stage}</p></div></div>))}
          </div>
        </TabsContent>

        <TabsContent value="comments">
          <div className="rounded-2xl border border-border bg-surface-raised p-5">
            <div className="mb-4 flex gap-2">
              <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment or @mention…" className="rounded-full" data-testid="comment-input" onKeyDown={(e) => e.key === "Enter" && addComment()} />
              <Button onClick={addComment} data-testid="comment-submit" className="rounded-full bg-gradient-to-r from-brand-violet to-brand-pink text-white">Post</Button>
            </div>
            <div className="space-y-4">
              {campComments.length === 0 ? <p className="flex items-center gap-2 text-sm text-muted-foreground"><ChatCircle size={16} /> No comments yet.</p> : campComments.map((cm) => (
                <div key={cm.id} className="flex items-start gap-3"><Avatar className="h-8 w-8"><AvatarImage src={cm.avatar} /><AvatarFallback>{cm.author?.[0]}</AvatarFallback></Avatar><div><p className="text-sm"><span className="font-semibold">{cm.author}</span> <span className="text-muted-foreground">· {new Date(cm.created_at).toLocaleString()}</span></p><p className="text-sm">{cm.text}</p></div></div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <div className="space-y-3">
            {activity.slice(0, 8).map((a) => (<div key={a.id} className="flex items-start gap-3 rounded-xl border border-border bg-surface-raised p-3"><Avatar className="h-8 w-8"><AvatarImage src={a.avatar} /><AvatarFallback>{a.user?.[0]}</AvatarFallback></Avatar><p className="text-sm"><span className="font-semibold">{a.user}</span> <span className="text-muted-foreground">{a.action}</span></p></div>))}
          </div>
        </TabsContent>
      </Tabs>

      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="Delete this campaign?" description="This will remove the campaign permanently." onConfirm={del} />
    </div>
  );
}
